/* eslint-disable complexity */
import React from 'react';

import { PhoneCallRoute, useRouter } from '@search/offer-search-routes/src';
import { CALLTOUCH_ID } from '@search/classified-common/src/scripts/useCalltouch';
import { SellerType } from '@search/filter-enums/enums';
import { OfferType, useOfferQuery } from '../../../../domain/offer/search/page/OfferQuery';
import { sellerCompat, priceCompat, OfferSnippetModel, useOfferCardLink } from '../../../../domain/offer/search/page/SearchOffersQuery';
import { useOfferPhoneFetch } from '../../../../domain/offer/search/page/OfferPhoneQuery';
import { useClassifiedOfferDataLayerPush } from '../../../../domain/offer/analytics/ClassifiedOfferGtmContext';
import { formatCallbackMailExtra } from '../../tools/formatCallbackMailExtra';
import { useReportMutation } from '../ReportButton/ReportMutation';

export function useOfferPhoneModalOpener({ offerId, isMobile, callTouchEnabled, isCard }: {
    offerId: string;
    isMobile?: boolean;
    isCard?: boolean;
    callTouchEnabled?: boolean | null;
}) {
    const callTouchId = callTouchEnabled ? CALLTOUCH_ID : undefined;
    const phoneCallRoute = useRouter().route(PhoneCallRoute);
    const [ isPhoneModalOpened, setIsPhoneModalOpened ] = React.useState(false);

    const closePhoneModal = React.useCallback((e?: React.MouseEvent | React.KeyboardEvent) => {
        // e?.stopPropagation();
        setIsPhoneModalOpened(false);
        timerRef.current && clearTimeout(timerRef.current);
        timerRef.current = undefined;
    }, []);

    // eslint-disable-next-line no-undef
    const timerRef = React.useRef<NodeJS.Timeout>();

    React.useEffect(() => {
        return () => {
            timerRef.current && clearTimeout(timerRef.current);
            timerRef.current = undefined;
        };
    }, []);

    const phoneFetch = useOfferPhoneFetch();

    const openPhoneModal = React.useCallback(async (delay = 5000) => {
        // e?.stopPropagation();
        if (delay === 0) return setIsPhoneModalOpened(true);
        if (! isMobile) return setIsPhoneModalOpened(true);

        if (timerRef.current) return;

        timerRef.current = setTimeout(() => {
            setIsPhoneModalOpened(true);
        }, delay);

        if (isMobile && offerId) {
            const phone = await phoneFetch({ offerId, callTouchId, isCard });

            window.open(phone.href, '_self');
        }
    }, [ isMobile, phoneCallRoute, offerId, phoneFetch, callTouchId, isCard ]);

    return {
        closePhoneModal,
        openPhoneModal,
        isPhoneModalOpened
    };
}

export function useOfferPhoneModal({
    offer: offerCard,
    snippet,
    offerId = offerCard?.id ?? snippet?.id,
    isMobile
}: {
    offerId?: string;
    isMobile?: boolean;
    offer?: OfferType;
    snippet?: OfferSnippetModel;
}) {
    const [ status, setStatus ] = React.useState<
        'start' | 'call-to-action' | 'report' | 'ask-callback' |
        'success' | 'error' | 'online-purchase' |
        'consult-online-purchase'
    >('start');

    const loader = useOfferQuery();

    const responseSnippet = offerId && ! offerCard && ! snippet ? loader({
        offerId,
        skipNotMain: true
    }) : undefined;
    const offer = (
        responseSnippet?.data?.offer.__typename === 'ProfOfferSnippet' ? responseSnippet.data.offer : undefined
    ) ?? offerCard;
    const receivedOffer = offer ?? snippet;

    const dataLayerPush = useClassifiedOfferDataLayerPush();
    const seller = receivedOffer ? sellerCompat(receivedOffer.seller) : undefined;
    const hasAnyPriority = receivedOffer?.status?.hasAnyPriority;

    const newBuildingEmailExtra = receivedOffer ? formatCallbackMailExtra(receivedOffer, 'offer') : '';

    const handleSuccess = React.useCallback((e?: React.MouseEvent<Element, MouseEvent>) => {
        e?.stopPropagation();
        dataLayerPush({ event: 'cl_showtel_modal_info_verif_yes' });
        setStatus('success');
    }, [ dataLayerPush ]);

    const handleReport = React.useCallback((e?: React.MouseEvent<Element, MouseEvent>) => {
        e?.stopPropagation();
        dataLayerPush({ event: 'cl_showtel_modal_info_verif_no' });
        setStatus('report');
    }, [ dataLayerPush ]);

    const reportMutation = useReportMutation();

    const [ isReporting, setIsReporting ] = React.useState(false);

    const handleNoCall = React.useCallback((e?: React.MouseEvent<Element, MouseEvent>) => {
        e?.stopPropagation();

        if (! offerId) return;

        dataLayerPush({ event: 'cl_showtel_modal_info_verif_silence' });
        if (hasAnyPriority) {
            setStatus('ask-callback');
            return;
        }

        setIsReporting(true);

        reportMutation({
            offerId,
            banReason: 'RELEVANCE_NO_REPLY',
            comment: 'Жалоба отправлена с формы показа телефона'
        })
            .then(() => {
                setIsReporting(false);
                setStatus('success');
            })
            .catch(err => {
                window.console.error(err);
                setIsReporting(false);
                setStatus('error');
            });
    }, [ hasAnyPriority, offerId, reportMutation, dataLayerPush ]);

    const handleCallback = React.useCallback((e?: React.MouseEvent<Element, MouseEvent>) => {
        e?.stopPropagation();
        dataLayerPush({ event: 'cl_showtel_modal_info_verif_silent_callback_click' });
        setStatus('call-to-action');
    }, [ dataLayerPush ]);

    const showBuyOnline = React.useCallback(() => {
        dataLayerPush({ event: 'cl_showtel_modal_onlinebuy_click' });
        setStatus('online-purchase');
    }, [ dataLayerPush ]);

    const showConsultOnlinePurchase = React.useCallback(() => {
        setStatus('consult-online-purchase');
    }, [ ]);

    const dt = receivedOffer?.dealType;
    const ro = receivedOffer?.realtyObject;
    const price = priceCompat(dt);

    const isNewBuilding = (ro?.__typename === 'ProfOfferFlat' && ro.building?.isNewbuilding) ?? false;

    // RS-6980: Степа попросил откатить модалки до ипотеки
    const isBuyButton = false;

    const { offerUrl } = useOfferCardLink(receivedOffer);

    return {
        price,
        error: responseSnippet?.errors?.[0],
        isNewBuilding,
        isBuyButton,
        showBuyOnline,
        handleCallback,
        handleNoCall,
        isReporting,
        isLoading: responseSnippet?.loading ?? false,
        handleReport,
        handleSuccess,
        seller,
        receivedOffer,
        newBuildingEmailExtra,
        status,
        offerUrl,
        realtyObject: ro,
        showConsultOnlinePurchase
    };
}
